import { useEffect, useMemo, useState } from "react";
import { Avatar } from "@/components/avatar";
import { Sparkles } from "lucide-react";

const MIN_MS = 7000;
const MAX_MS = 9000;

export function OnboardingSetupBridge({
  names,
  ready,
  onDone,
}: {
  names: string[];
  ready: boolean;
  onDone: () => void;
}) {
  const firstName = (names[0]?.split(" ")[0] ?? "your people").trim() || "your people";
  const others = Math.max(0, names.length - 1);
  const savingLine = others > 0 ? `Saving ${firstName} +${others} more…` : `Saving ${firstName}…`;
  const lines = [
    "Setting up your circle…",
    savingLine,
    `Pulling public context for ${firstName}…`,
    "Tuning Pyng to your people…",
  ];
  const [lineIdx, setLineIdx] = useState(0);
  const [startedAt] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setLineIdx((i) => Math.min(i + 1, lines.length - 1));
    }, 1100);
    return () => clearInterval(id);
  }, [lines.length]);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= MAX_MS) return onDone();
      if (elapsed >= MIN_MS && ready) return onDone();
      setTimeout(tick, 120);
    };
    const t = setTimeout(tick, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [startedAt, ready, onDone]);

  // place avatars around a circle
  const ring = names.slice(0, 6);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const ringStyle = useMemo(
    () => ({ borderColor: "color-mix(in oklab, var(--accent-neon) 22%, transparent)" }),
    [],
  );
  const ringInnerStyle = useMemo(
    () => ({ borderColor: "color-mix(in oklab, var(--accent-neon) 34%, transparent)" }),
    [],
  );

  return (
    <div className="flex flex-col items-center justify-center py-6 animate-in fade-in">
      <div className="relative h-44 w-44">
        {/* orbit rings — neon, consistent with the + overlay */}
        <div
          className="absolute inset-0 rounded-full border"
          style={ringStyle}
        />
        <div
          className="absolute inset-[14px] rounded-full border"
          style={ringInnerStyle}
        />

        {/* glowing neon core planet — same as OrbitThinkingOverlay */}
        <div
          className={`absolute inset-[40px] rounded-full ${reduced ? "" : "animate-pulse"}`}
          style={{
            background:
              "radial-gradient(circle at 32% 28%, color-mix(in oklab, var(--accent-neon) 90%, white), color-mix(in oklab, var(--accent-neon) 55%, transparent))",
            boxShadow:
              "0 0 28px color-mix(in oklab, var(--accent-neon) 55%, transparent), 0 0 0 1px color-mix(in oklab, var(--accent-neon) 45%, transparent)",
          }}
        />

        {/* a fast satellite on the inner ring */}
        <div
          className={`absolute inset-[14px] ${reduced ? "" : "animate-[spin_6s_linear_infinite_reverse]"}`}
          aria-hidden
        >
          <div
            className="absolute left-1/2 h-[7px] w-[7px] -translate-x-1/2 rounded-full"
            style={{
              top: -3.5,
              background: "var(--accent-neon)",
              boxShadow: "0 0 10px color-mix(in oklab, var(--accent-neon) 70%, transparent)",
            }}
          />
        </div>

        {/* orbiting avatars — whole ring slowly rotates, avatars stay upright */}
        <div
          className="absolute inset-0"
          style={{ animation: reduced ? undefined : "spin 24s linear infinite" }}
        >
          {ring.map((n, i) => {
            const angle = (i / ring.length) * Math.PI * 2 - Math.PI / 2;
            const r = 78;
            const x = 88 + Math.cos(angle) * r - 14;
            const y = 88 + Math.sin(angle) * r - 14;
            return (
              <div
                key={`${n}-${i}`}
                className="absolute animate-in fade-in zoom-in"
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                  animationDelay: `${i * 90}ms`,
                }}
              >
                <div style={{ animation: reduced ? undefined : "spin 24s linear infinite reverse" }}>
                  <Avatar name={n} size={28} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-7 h-5 text-center">
        <p
          key={lineIdx}
          className="text-sm text-foreground/85 animate-in fade-in"
        >
          {lines[lineIdx]}
        </p>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">This only takes a moment.</p>
    </div>
  );
}
